import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppStore, type IAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import getCurrentUserId from '@/utils/user/getCurrentUserId';
import {
  getLikedPresets,
  saveLikedPresets,
} from '@/requests/cloud-storage/likedPresets';

import likesKeys from '../api/likesKeys';

import type { LikedPresets } from '@/requests/cloud-storage/likedPresets';

const selector = (store: IAppStore) => store.toolId;

/**
 * The document ids this user liked, from their Drive file. One read per session
 * answers "is it liked" for every heart on screen — the likes service records
 * the like but is never asked for per-preset state.
 */
const useMyLikedIds = () => {
  const toolId = useAppStore(selector);
  const { isAuthorized } = useAuth();
  const queryClient = useQueryClient();

  const userId = getCurrentUserId();
  const queryKey = useMemo(() => likesKeys.liked(userId), [userId]);

  const { data, isFetching } = useQuery<LikedPresets | null>({
    queryKey,
    queryFn: () => getLikedPresets({ toolId }),
    enabled: isAuthorized && Boolean(toolId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const likedIds = useMemo(
    () => new Set(data?.documentIds ?? []),
    [data?.documentIds]
  );

  const mutation = useMutation({
    scope: { id: 'liked-presets' },
    mutationFn: (nextIds: string[]) => {
      const cached = queryClient.getQueryData<LikedPresets | null>(queryKey);
      return saveLikedPresets({
        toolId,
        documentIds: nextIds,
        fileId: cached?.fileId ?? null,
      });
    },
    onSuccess: (result) => {
      // null = write failed (already toasted) → resync from Drive.
      if (!result) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }
      if (result.fileId) {
        queryClient.setQueryData<LikedPresets | null>(queryKey, (prev) =>
          prev ? { ...prev, fileId: result.fileId } : prev
        );
      }
    },
    onError: () => queryClient.invalidateQueries({ queryKey }),
  });

  /** Cache only, so the heart answers the tap while the write waits. */
  const writeLiked = useCallback(
    ({ documentId, isLiked }: { documentId: string; isLiked: boolean }) => {
      queryClient.setQueryData<LikedPresets | null>(queryKey, (prev) => {
        const currentIds = prev?.documentIds ?? [];

        return {
          // Newest first, so the profile's Liked grid needs no timestamps.
          documentIds: isLiked
            ? [documentId, ...currentIds.filter((id) => id !== documentId)]
            : currentIds.filter((id) => id !== documentId),
          fileId: prev?.fileId ?? null,
        };
      });
    },
    [queryClient, queryKey]
  );

  /**
   * Flips a preset and reports where it landed, reading the cache rather than
   * the last render: `likedIds` only reaches a handler after React re-renders,
   * so two taps inside one commit would both see the state before either.
   */
  const toggleLiked = useCallback(
    (documentId: string) => {
      // A read still in flight would land on top of the write below and drop the
      // tap, and the debounced save would then store the state just left.
      queryClient.cancelQueries({ queryKey });

      const current = queryClient.getQueryData<LikedPresets | null>(queryKey);
      const isLiked = (current?.documentIds ?? []).includes(documentId);

      writeLiked({ documentId, isLiked: !isLiked });

      return !isLiked;
    },
    [queryClient, queryKey, writeLiked]
  );

  /**
   * Writes whatever the cache holds now — every tap since is already folded in.
   * Resolves to null when the write failed, so the caller can hold back the
   * global like rather than counting one the user has no record of.
   */
  const persistLiked = useCallback(() => {
    const current = queryClient.getQueryData<LikedPresets | null>(queryKey);

    return mutation.mutateAsync(current?.documentIds ?? []);
  }, [mutation, queryClient, queryKey]);

  return {
    likedIds,
    writeLiked,
    toggleLiked,
    persistLiked,
    isLoading: isAuthorized && isFetching && !data,
  };
};

export default useMyLikedIds;
